"use client";

import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { PlusIcon, XIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "../lib/cn";
import { Button } from "./button";

/**
 * One open workspace, rendered as a tab. In this product ONE WORKSPACE == ONE
 * TAB, so `id` is the workspace id.
 */
export interface WorkspaceTabItem {
  id: string;
  title: string;
  /** Workspace kind (`canvas` | `page`), shown as a subtle glyph/hint. */
  kind?: string;
  /** Ephemeral canvases are discarded when closed. */
  ephemeral?: boolean;
  /** Unsaved local changes (dot indicator). */
  dirty?: boolean;
}

export interface WorkspaceTabsProps {
  tabs: WorkspaceTabItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onReorder: (ids: string[]) => void;
  onAdd: () => void;
  className?: string;
}

/**
 * A closable, reorderable terminal tab strip.
 *
 * Built on Base UI `Tabs` so the strip keeps proper `tablist`/`tab`
 * semantics, roving tab index, and arrow-key navigation. Reordering is native
 * HTML5 drag-and-drop (no extra dependency); the close control is a real
 * button nested in the (non-button) tab element, hence `nativeButton={false}`
 * + a `<div>` render target.
 */
export function WorkspaceTabs({
  tabs,
  activeId,
  onSelect,
  onClose,
  onReorder,
  onAdd,
  className,
}: WorkspaceTabsProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const dropOn = (targetId: string) => {
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const ids = tabs.map((tab) => tab.id);
    if (!ids.includes(sourceId) || !ids.includes(targetId)) return;
    const next = ids.filter((id) => id !== sourceId);
    const insertAt = next.indexOf(targetId);
    next.splice(insertAt, 0, sourceId);
    onReorder(next);
  };

  return (
    <TabsPrimitive.Root
      value={activeId ?? null}
      onValueChange={(value) => {
        if (typeof value === "string") onSelect(value);
      }}
      className={cn("flex min-w-0 flex-col", className)}
    >
      <div className="flex min-w-0 items-center gap-1 border-b bg-muted/30 px-1">
        <TabsPrimitive.List
          aria-label="Open workspaces"
          className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto"
        >
          {tabs.map((tab) => {
            const active = tab.id === activeId;
            return (
              <TabsPrimitive.Tab
                key={tab.id}
                value={tab.id}
                nativeButton={false}
                render={<div />}
                draggable
                onDragStart={(event) => {
                  setDragId(tab.id);
                  if (event.dataTransfer) {
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", tab.id);
                  }
                }}
                onDragOver={(event) => {
                  if (!dragId || dragId === tab.id) return;
                  event.preventDefault();
                  if (event.dataTransfer)
                    event.dataTransfer.dropEffect = "move";
                  if (overId !== tab.id) setOverId(tab.id);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  dropOn(tab.id);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
                className={cn(
                  "group/tab relative flex h-8 max-w-56 shrink-0 cursor-default items-center gap-1.5 rounded-t-md border border-b-0 border-transparent px-2.5 text-sm text-muted-foreground transition-colors outline-none select-none",
                  "hover:bg-background/60 hover:text-foreground",
                  "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                  active && "border-border bg-background text-foreground",
                  overId === tab.id && "bg-muted",
                  dragId === tab.id && "opacity-60",
                )}
              >
                {tab.kind ? (
                  <span
                    aria-hidden
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      tab.kind === "page" ? "bg-sky-500" : "bg-violet-500",
                    )}
                  />
                ) : null}
                <span className="min-w-0 truncate">{tab.title}</span>
                {tab.ephemeral ? (
                  <span
                    title="Ephemeral"
                    className="shrink-0 text-[10px] tracking-wide text-muted-foreground/70 uppercase"
                  >
                    eph
                  </span>
                ) : null}
                {tab.dirty ? (
                  <span
                    title="Unsaved changes"
                    aria-label="Unsaved changes"
                    className="size-1.5 shrink-0 rounded-full bg-amber-500"
                  />
                ) : null}
                <button
                  type="button"
                  aria-label={`Close ${tab.title}`}
                  className={cn(
                    "ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground/70 opacity-0 transition hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-ring/50 focus-visible:outline-none group-hover/tab:opacity-100",
                    active && "opacity-100",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClose(tab.id);
                  }}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <XIcon className="size-3" />
                </button>
              </TabsPrimitive.Tab>
            );
          })}
        </TabsPrimitive.List>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Open workspace"
          title="Open workspace"
          data-testid="workspace-tab-add"
          onClick={onAdd}
          className="shrink-0"
        >
          <PlusIcon />
        </Button>
      </div>
    </TabsPrimitive.Root>
  );
}
